import { MotionConfig, motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Toaster } from '@/components/ui/Toaster';
import { ConversionPanel } from '@/features/converter/ConversionPanel';
import { ResultCard } from '@/features/download/ResultCard';
import { SettingsPanel } from '@/features/editor/SettingsPanel';
import { PreviewSection } from '@/features/preview/PreviewSection';
import { resetProject } from '@/features/project/projectActions';
import { ReplaceVideoButton } from '@/features/upload/ReplaceVideoButton';
import { UploadDropzone } from '@/features/upload/UploadDropzone';
import { useTheme } from '@/hooks/useTheme';
import { useProjectStore } from '@/store/projectStore';

function Header() {
  const hasSource = useProjectStore((state) => state.sourceUrl !== null);
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-4 sm:px-6">
      <div className="flex items-baseline gap-2.5">
        <span className="font-display text-lg font-bold tracking-tight">loopsmith</span>
        <span className="eyebrow hidden sm:inline">video → gif</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {hasSource && (
          <>
            <ReplaceVideoButton />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm('Discard this video and start over?')) resetProject();
              }}
            >
              Start over
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
          className="px-2"
        >
          {theme === 'dark' ? (
            <Sun aria-hidden className="size-4" />
          ) : (
            <Moon aria-hidden className="size-4" />
          )}
        </Button>
      </div>
    </header>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-2xl pt-6 sm:pt-14">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div className="space-y-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            Make a GIF worth looping.
          </h1>
          <p className="max-w-lg text-sm leading-relaxed text-ink-500 sm:text-base dark:text-ink-400">
            Trim, crop and tune any video, then encode it with a two-pass color palette — right here
            in your browser. The file never leaves this device.
          </p>
        </div>
        <UploadDropzone />
      </motion.div>
    </div>
  );
}

function Editor() {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
      className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]"
    >
      <motion.div
        variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
        className="min-w-0 space-y-4"
      >
        <PreviewSection />
        <ResultCard />
      </motion.div>
      <motion.div
        variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
        className="space-y-4"
      >
        <SettingsPanel />
        <ConversionPanel />
      </motion.div>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-xs text-ink-400 sm:px-6 dark:text-ink-500">
      <span>Runs entirely in your browser · FFmpeg.wasm</span>
      <span>No uploads · No tracking</span>
    </footer>
  );
}

export default function App() {
  const hasSource = useProjectStore((state) => state.sourceUrl !== null);

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex min-h-dvh flex-col">
        <Header />
        <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 sm:px-6">
          {hasSource ? <Editor /> : <EmptyState />}
        </main>
        <Footer />
        <Toaster />
      </div>
    </MotionConfig>
  );
}
